/**
 * Writing cards. Edits re-broadcast to every session playing the pack, so a card added
 * mid-game is in the next deal.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ApplesCardKind } from '@gsf/games';
import { addCard, listCards, listDecks, removeCards, type DeckCard } from './content';

export function DeckEditor({
  gameId,
  notify,
}: {
  gameId: string;
  notify: (message: string) => void;
}) {
  const [packId, setPackId] = useState<string | null>(null);
  const [cards, setCards] = useState<DeckCard[]>([]);
  const [draft, setDraft] = useState('');
  const [kind, setKind] = useState<ApplesCardKind>('red');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (id: string) => {
      try {
        setCards(await listCards(id));
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Could not load the deck.');
      }
    },
    [notify],
  );

  // A snapshot doesn't carry the session's pack, so this asks for the game's first deck,
  // the one `session/create.ts` chose.
  useEffect(() => {
    if (!open || packId !== null) return;
    void (async () => {
      try {
        const decks = await listDecks(gameId);
        const first = decks[0];
        if (!first) {
          notify('This server has no deck for Apples to Apples. Run `npm run seed`.');
          return;
        }
        setPackId(first.id);
        await load(first.id);
      } catch (err) {
        notify(err instanceof Error ? err.message : 'Could not find the deck.');
      }
    })();
  }, [open, packId, gameId, load, notify]);

  const greens = cards.filter((card) => card.kind === 'green');
  const reds = cards.filter((card) => card.kind === 'red');

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const text = draft.trim();
    if (!text || packId === null) return;
    setBusy(true);
    try {
      // The server validates against the game's schema; a too-long card comes back as its complaint.
      await addCard(packId, kind, text);
      setDraft('');
      await load(packId);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Could not add that card.');
    } finally {
      setBusy(false);
    }
  }

  async function drop(card: DeckCard) {
    if (packId === null) return;
    setBusy(true);
    try {
      await removeCards([card.id]);
      await load(packId);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Could not delete that card.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="panel col">
        <h2 className="panel-title">The deck</h2>
        <button className="btn btn-sm" type="button" onClick={() => setOpen(true)}>
          Edit cards
        </button>
      </div>
    );
  }

  return (
    <div className="panel col">
      <div className="row">
        <h2 className="panel-title" style={{ margin: 0 }}>
          The deck
        </h2>
        <span className="faint">
          {greens.length} green · {reds.length} red
        </span>
        <span className="spacer" />
        <button className="btn btn-sm btn-ghost" type="button" onClick={() => setOpen(false)}>
          Close
        </button>
      </div>

      <form className="row" onSubmit={submit} style={{ gap: '0.4rem' }}>
        <select
          value={kind}
          onChange={(event) => setKind(event.target.value as ApplesCardKind)}
          aria-label="Card colour"
        >
          <option value="red">Red (a thing)</option>
          <option value="green">Green (a word)</option>
        </select>
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={kind === 'green' ? 'Squishy' : 'A wet dog'}
          autoComplete="off"
        />
        <button className="btn btn-sm btn-primary" type="submit" disabled={busy || !draft.trim()}>
          Add
        </button>
      </form>

      <div className="apples-deck">
        {(['green', 'red'] as const).map((colour) => (
          <div key={colour} className="col" style={{ gap: '0.3rem', minWidth: 0 }}>
            <span className="faint">{colour === 'green' ? 'Green apples' : 'Red apples'}</span>
            <ul className="apples-deck-list">
              {(colour === 'green' ? greens : reds).map((card) => (
                <li key={card.id}>
                  <span className="apples-deck-text">{card.text}</span>
                  <span className="spacer" />
                  <button
                    className="btn btn-sm btn-ghost"
                    type="button"
                    title="Delete this card"
                    disabled={busy}
                    onClick={() => void drop(card)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
